import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Stack, Typography,
  TextField, InputAdornment
} from '@mui/material';
import { settlementsApi } from '../../services/api';
import { useNotification } from '../../hooks/useNotification';
import LoadingButton from '../LoadingButton';
import { toPaise } from '../../utils/splitMath';
import { formatMoney, currencySymbol } from '../../utils/money';

const formatAmount = (value) => formatMoney(Number(value));

// Record a payment with one person. `target` is { email, name, balance, breakdown }
// where a positive balance means they owe you.
function SettleUpDialog({ open, target, onClose, onSettled }) {
  const [amount, setAmount] = useState('');
  const [settling, setSettling] = useState(false);
  const { showSuccess, showError } = useNotification();

  useEffect(() => {
    if (open && target) setAmount(Math.abs(target.balance).toFixed(2));
  }, [open, target]);

  const openAmount = target ? Math.abs(target.balance) : 0;
  const amountPaise = toPaise(amount);
  const amountError = !Number.isFinite(amountPaise) || amountPaise <= 0
    ? 'Enter an amount greater than zero'
    : amountPaise > toPaise(openAmount)
      ? `Cannot be more than ${formatAmount(openAmount)}`
      : '';

  const handleSettle = async () => {
    if (!target || amountError) return;
    try {
      setSettling(true);
      const response = await settlementsApi.settle(target.email, Number(amount), null);
      showSuccess(response.data.fullySettled ? 'Settled up!' : 'Partial payment recorded');
      onClose();
      if (onSettled) await onSettled();
    } catch (error) {
      showError(error.response?.data?.error || 'Failed to settle');
    } finally {
      setSettling(false);
    }
  };

  const name = target ? target.name || target.email : '';
  const theyPay = target ? target.balance > 0 : false;

  return (
    <Dialog open={open && !!target} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Settle up with {name}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {theyPay
              ? `${name} owes you ${formatAmount(openAmount)}. Record how much they paid you.`
              : `You owe ${name} ${formatAmount(openAmount)}. Record how much you paid them.`}
            {target?.breakdown?.length > 1 && ' The payment is split across your groups automatically.'}
          </Typography>
          <TextField
            label="Amount paid"
            type="number"
            fullWidth
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            error={!!amountError}
            helperText={amountError || (amountPaise < toPaise(openAmount) ? 'A smaller amount is recorded as a partial payment' : ' ')}
            slotProps={{
              input: {
                startAdornment: <InputAdornment position="start">{currencySymbol()}</InputAdornment>
              },
              htmlInput: { min: 0, step: '0.01' }
            }}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <LoadingButton onClick={handleSettle} variant="contained" loading={settling} disabled={!!amountError}>
          Record payment
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}

export default SettleUpDialog;
